// Question Generation run detail — mirrors the other operation detail pages
// (ConceptLabDetail/SolutionAnalysisDetail) using the shared operation-detail
// components. Generation has no student/question context (it authors from a
// PDF, not a student attempt), so those sections are dropped; we keep the
// generation request metadata, the model-run table, and the per-run prompt/
// response detail. Model runs are keyed by submissionId = jobId.
import { useEffect, useState } from 'react';
import { useParams, useNavigate , useLocation } from 'react-router-dom';
import { OperationNavStrip, type OperationNav } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import type { ModelOutput } from '../../types';
import {
    OperationDetailPage,
    ContextGrid,
    MetadataCard,
    MetaItem,
    RunsTable,
    RunDetailCard,
    formatLatency,
    usePromptLookup,
    buildDefaultRunColumns,
    buildRunDetailHeading,
    buildRunSummary,
    getRunError,
} from '@shared-ui/operation-detail';
import { ParsedFeedbackBody, getRawOutputText } from '@shared-ui/parsed-feedback';
import './SolutionAnalysisDetail.css';

// Two job types share this view — generation from a PDF and generation from
// existing questions. The fields each one fills differ, so everything below is
// optional and rendered only when present rather than shown as an empty row.
interface GenPayload {
    subject?: string;
    class?: string;
    variationsPerQuestion?: number;
    instructions?: string;
    sourceFileName?: string;
    questionSetId?: string;
    seed?: { kind?: string; questionIds?: string[]; userId?: string; moduleIds?: string[]; limit?: number };
    assignTo?: string[];
}
interface GenResult {
    createdQuestionIds?: string[];
    sourcePdf?: { questionUrl?: string; annotationUrl?: string };
    /** From-questions runs: how the seed set narrowed on its way to the model. */
    attempted?: number;
    previouslyVaried?: number;
    skipped?: Array<{ questionId: string; reason: string }>;
}

export function GenerationDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    /** The page of rows this detail was opened from, if it was opened from one.
     *  Absent on a direct link, which is why the strip is conditional. */
    const opNav = (location.state as { nav?: OperationNav } | null)?.nav;
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

    const { data: jobData, isLoading, error } = useQuery({
        queryKey: ['generationJob', id],
        queryFn: () => adminApi.getAsyncJobDetail(id!),
        enabled: !!id,
    });

    const { data: runs = [] } = useQuery({
        queryKey: ['modelRuns', id],
        queryFn: () => adminApi.getModelRuns(id!),
        enabled: !!id,
    });

    const { data: prompts } = useQuery({
        queryKey: ['prompts', 'includeArchived'],
        queryFn: () => adminApi.getPrompts({ includeArchived: true }),
    });
    const { getPromptName, getPromptType } = usePromptLookup(prompts);

    useEffect(() => {
        if (selectedRunId || runs.length === 0) return;
        const primary = runs.find(r => r.isPrimary) ?? runs[0];
        if (primary) setSelectedRunId(primary.id);
    }, [runs, selectedRunId]);

    const goBack = () => navigate('/submissions?tab=generation');

    if (isLoading) {
        return <div className="operation-detail-page"><div className="loading">Loading generation run…</div></div>;
    }
    if (error || !jobData) {
        return (
            <div className="operation-detail-page">
                <div className="error">Error loading generation run: {(error as Error)?.message || 'Not found'}</div>
                <button onClick={goBack}>Back to Question Generation</button>
            </div>
        );
    }

    const job = jobData.job;
    const payload = (job.payload ?? {}) as GenPayload;
    const result = (job.result ?? {}) as GenResult;
    const createdCount = result.createdQuestionIds?.length ?? 0;
    // Say what fed the run in the same words the list uses, so a row and its
    // detail page never disagree about what happened.
    const seedLabel = job.type === 'generate-question-variations'
        ? 'Uploaded PDF'
        : payload.seed?.kind === 'explicit'
            ? `${payload.seed.questionIds?.length ?? 0} selected question(s)`
            : payload.seed?.kind === 'mistakes'
                ? "A student's mistakes"
                : 'Existing questions';

    const promptLookup = { getPromptName, getPromptType };
    const runColumns = buildDefaultRunColumns<ModelOutput>(promptLookup, {
        returnTo: { label: 'Question Generation Detail' },
    });
    const selectedRun = runs.find(r => r.id === selectedRunId);
    const runDetailHeading = selectedRun && buildRunDetailHeading(selectedRun, promptLookup);
    const runSummary = selectedRun && buildRunSummary(selectedRun);
    const runError = selectedRun ? getRunError(selectedRun) : undefined;
    const selectedPromptType = selectedRun ? getPromptType(selectedRun.parameters.promptId) : undefined;
    const selectedRunRawResponse = selectedRun ? getRawOutputText(selectedRun) : null;
    const parsedHasContent = !!(selectedRun && (selectedRun.parsedAssessment || selectedRun.extractedData || selectedRunRawResponse));
    const parsedBody = selectedRun && parsedHasContent ? (
        <ParsedFeedbackBody
            promptType={selectedPromptType}
            feedback={selectedRun.parsedAssessment}
            extractedData={selectedRun.extractedData}
            rawResponse={selectedRunRawResponse}
        />
    ) : undefined;

    return (
        <OperationDetailPage
            title="Question Generation Details"
            id={job.id}
            backLabel="Question Generation"
            navSlot={opNav && <OperationNavStrip nav={opNav} noun="Run" />}
            onBack={goBack}
            context={
                <>
                <ContextGrid
                    left={
                        <MetadataCard title="Generation Request">
                            <MetaItem label="Seeded from">{seedLabel}</MetaItem>
                            {payload.seed?.kind === 'mistakes' && (
                                <>
                                    <MetaItem label="Student">{payload.seed.userId || '-'}</MetaItem>
                                    <MetaItem label="Chapters">{payload.seed.moduleIds?.length ?? 0}</MetaItem>
                                    <MetaItem label="Seed cap">{payload.seed.limit ?? '-'}</MetaItem>
                                </>
                            )}
                            {payload.subject && <MetaItem label="Subject"><span className="tag">{payload.subject}</span></MetaItem>}
                            {payload.class && <MetaItem label="Class"><span className="tag">{payload.class}</span></MetaItem>}
                            <MetaItem label="Variations / Q">{payload.variationsPerQuestion ?? '-'}</MetaItem>
                            {payload.sourceFileName && <MetaItem label="Source File">{payload.sourceFileName}</MetaItem>}
                            <MetaItem label="Instructions">{payload.instructions || '—'}</MetaItem>
                        </MetadataCard>
                    }
                    right={
                        <MetadataCard title="Run">
                            <MetaItem label="Status"><span className={`tag status-${job.status}`}>{job.status}</span></MetaItem>
                            <MetaItem label="Attempts">{job.attempts}/{job.maxAttempts}</MetaItem>
                            <MetaItem label="Latency">{formatLatency(job.latencyMs)}</MetaItem>
                            <MetaItem label="Questions Created">{createdCount}</MetaItem>
                            {result.attempted != null && (
                                <MetaItem label="Sources used">
                                    {result.attempted}
                                    {result.previouslyVaried ? ` (${result.previouslyVaried} varied before)` : ''}
                                </MetaItem>
                            )}
                            {!!result.skipped?.length && (
                                // The gap between "questions I expected" and
                                // "questions I got" is the first thing an admin
                                // asks about; naming the reasons answers it here
                                // instead of in the logs.
                                <MetaItem label="Skipped">
                                    {Object.entries(
                                        result.skipped.reduce<Record<string, number>>((acc, s) => {
                                            acc[s.reason] = (acc[s.reason] ?? 0) + 1;
                                            return acc;
                                        }, {}),
                                    ).map(([reason, n]) => `${n} ${reason}`).join(' · ')}
                                </MetaItem>
                            )}
                            <MetaItem label="Queued">
                                <span className="datetime">{format(new Date(job.queuedAt), 'MMM dd, HH:mm:ss')}</span>
                            </MetaItem>
                            {job.completedAt && (
                                <MetaItem label="Completed">
                                    <span className="datetime">{format(new Date(job.completedAt), 'MMM dd, HH:mm:ss')}</span>
                                </MetaItem>
                            )}
                            {job.lastError && (
                                <MetaItem label="Error"><span className="tag status-failed">{job.lastError.message}</span></MetaItem>
                            )}
                        </MetadataCard>
                    }
                />
                {/* Custom section: source PDF + a link to the generated questions. */}
                <MetadataCard title="Generated Output">
                    {payload.questionSetId && (
                        <MetaItem label="Assignment">
                            <a
                                role="button"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/question-sets/${payload.questionSetId}`)}
                            >
                                Open the set this filled →
                            </a>
                        </MetaItem>
                    )}
                    <MetaItem label="Source PDF">
                        {result.sourcePdf?.questionUrl ? (
                            <span>
                                <a href={result.sourcePdf.questionUrl} target="_blank" rel="noopener noreferrer">Question PDF ↗</a>
                                {result.sourcePdf.annotationUrl && (
                                    <> · <a href={result.sourcePdf.annotationUrl} target="_blank" rel="noopener noreferrer">Annotation PDF ↗</a></>
                                )}
                            </span>
                        ) : (
                            <span className="muted">not retained for this run</span>
                        )}
                    </MetaItem>
                    <MetaItem label="Questions">
                        {createdCount > 0 ? (
                            <a
                                role="button"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/questions?batch=${job.id}&source=teacher`)}
                            >
                                Show {createdCount} generated question{createdCount === 1 ? '' : 's'} →
                            </a>
                        ) : (
                            <span className="muted">none created</span>
                        )}
                    </MetaItem>
                </MetadataCard>
                </>
            }
            runs={runs.length > 0 ? (
                <RunsTable
                    rows={runs}
                    columns={runColumns}
                    rowKey={r => r.id}
                    selectedRowId={selectedRunId}
                    onSelect={r => setSelectedRunId(r.id)}
                    getRowClass={r => [r.isPrimary ? 'primary' : '', r.errors?.length ? 'has-error' : ''].filter(Boolean).join(' ')}
                />
            ) : (
                <div className="card"><p className="muted">No model runs recorded for this run. Runs authored before execution logging was added won&apos;t appear here — re-run to capture the prompt execution.</p></div>
            )}
            runDetail={
                selectedRun && (
                    <RunDetailCard
                        heading={runDetailHeading}
                        error={runError}
                        errorRecords={selectedRun.errorRecords}
                        summary={runSummary}
                        systemPrompt={selectedRun.systemPromptText}
                        userPrompt={selectedRun.promptText}
                        rawResponse={selectedRunRawResponse}
                        rawOutputFull={selectedRun.rawOutputFull}
                        reasoningSummary={selectedRun.reasoningSummaryText}
                        modelInputId={selectedRun.inputId}
                        modelOutputId={selectedRun.id}
                        parsed={parsedBody}
                    />
                )
            }
        />
    );
}
